import type { DesignContestDto, JuryRankedEntryDto } from "../../../autogen/bff-api";

export const rankedDraft = (saved: readonly JuryRankedEntryDto[], entryIds: readonly string[]): JuryRankedEntryDto[] => [
  ...saved.filter((ranked) => entryIds.includes(ranked.bidId)),
  ...entryIds.filter((id) => !saved.some((ranked) => ranked.bidId === id)).map((bidId) => ({ bidId, assessment: "" })),
];

// Mirrors DesignContest.reportConfirmedByWholeJury in the BFF.
export const juryProgress = (contest: DesignContestDto): { done: number; total: number } => {
  const total = (contest.jury ?? []).length;
  const confirmed = new Set((contest.juryConfirmations ?? []).map((confirmation) => confirmation.memberIndex));
  return { done: contest.juryReport ? confirmed.size : 0, total };
};

// Mirrors JuryReport.entriesWithoutAssessment in the BFF.
export const entriesWithoutAssessment = (report: { rankedEntries: readonly JuryRankedEntryDto[] } | null | undefined) =>
  (report?.rankedEntries ?? []).filter((ranked) => !ranked.assessment.trim()).map((ranked) => ranked.bidId);

export const juryReportConfirmed = (contest: DesignContestDto | null | undefined): boolean => {
  if (!contest?.juryReport) return false;
  const { done, total } = juryProgress(contest);
  return total > 0 && done >= total;
};

export interface JuryDraft {
  ranking: JuryRankedEntryDto[];
  remarks: string;
  questions: string;
}

const draftKey = (eventId: string, savedAt: string | null | undefined) => `jury-draft:${eventId}:${savedAt ?? "none"}`;

export const readJuryDraft = (eventId: string, savedAt: string | null | undefined): JuryDraft | null => {
  try {
    const raw = sessionStorage.getItem(draftKey(eventId, savedAt));
    return raw ? (JSON.parse(raw) as JuryDraft) : null;
  } catch {
    return null;
  }
};

export const writeJuryDraft = (eventId: string, savedAt: string | null | undefined, draft: JuryDraft | null): void => {
  try {
    if (draft) sessionStorage.setItem(draftKey(eventId, savedAt), JSON.stringify(draft));
    else sessionStorage.removeItem(draftKey(eventId, savedAt));
  } catch {
    return;
  }
};

// Mirrors DesignContest.flaggedAmong in the BFF.
export const flaggedAmong = (contest: DesignContestDto | null | undefined, bidIds: readonly string[]): string[] =>
  [...new Set(bidIds)].filter((id) => (contest?.entryFlags ?? []).some((flag) => flag.bidId === id));
